/**
 * Live chat transport (Socket.IO).
 *
 * Used when talking to the real NestJS ChatModule gateway. In mock mode the
 * chat screen falls back to REST polling instead.
 */

import { io, type Socket } from "socket.io-client";

import type { ChatMessage } from "@/types";
import { SOCKET_URL } from "./config";
import { SOCKET_RECONNECT_OPTIONS } from "./socket-options";
import { READY_EVENT, socketRecovery } from "./socket-recovery";

export type ChatSocketHandlers = {
  onMessage?: (message: ChatMessage) => void;
  /**
   * A reaction changed on a message that is already on screen. Separate from
   * `onMessage` because nothing about the thread's length changed — only one
   * existing bubble did.
   */
  onReaction?: (message: ChatMessage) => void;
  /**
   * The socket (re)connected. Fires on the first connect AND on every automatic
   * reconnect, which is the moment the screen has to find out what it missed
   * while it was away.
   */
  onConnect?: () => void;
  onTyping?: (userId: string, isTyping: boolean) => void;
  onRevealUpdate?: (
    status: "pending_peer" | "revealed",
    userId?: string,
  ) => void;
  /**
   * The photo exchange moved. Carries no asset ids by design — the client
   * re-reads the authenticated endpoint, so nothing that could reference a
   * photo ever travels over the socket.
   */
  onPhotoExchange?: (actorId?: string) => void;
};

export function createChatSocket(
  chatId: string,
  token: string,
  handlers: ChatSocketHandlers,
) {
  const socket: Socket = io(`${SOCKET_URL}/chat`, {
    transports: ["websocket"],
    // Shared so sockets that CAN share a Manager do, and so a fleet-wide
    // reconnect spreads instead of arriving in one second. Sharing is not
    // guaranteed — recreating a namespace gets a new Manager (see
    // socket-options.ts) — which is why recovery is scoped per Manager.
    ...SOCKET_RECONNECT_OPTIONS,
    auth: { token },
    query: { chatId },
  });

  socket.on("connect", () => {
    // Re-joining on every connect, not just the first, is what makes a dropped
    // connection recoverable: socket.io reconnects on its own, but the room
    // membership is per-connection and does not come back with it. The join
    // is one socket frame — cheap enough to send before admission is known,
    // and simply dropped if the server refuses.
    socket.emit("join", { chatId });
  });
  // Catch-up is HTTP (up to a whole paginated re-read), so it waits for the
  // server's post-admission READY — the bare connect ack precedes admission,
  // and on a shed every recovery attempt would re-read the thread at a server
  // refusing for load.
  socket.on(READY_EVENT, () => {
    handlers.onConnect?.();
  });
  socket.on("message", (msg: ChatMessage) => handlers.onMessage?.(msg));
  socket.on("reaction", (msg: ChatMessage) => handlers.onReaction?.(msg));
  socket.on("typing", (p: { userId: string; isTyping?: boolean }) =>
    handlers.onTyping?.(p.userId, p.isTyping ?? true),
  );
  socket.on("reveal", (p: { status: "pending_peer" | "revealed"; userId?: string }) =>
    handlers.onRevealUpdate?.(p.status, p.userId),
  );
  socket.on("photo_exchange", (p: { actorId?: string }) =>
    handlers.onPhotoExchange?.(p.actorId),
  );

  // A server REFUSAL drops this socket's whole Manager connection; the
  // coordinator recovers that Manager's domain by reconnecting THIS instance,
  // so the `connect` handler above re-joins the room and the screen catches
  // up — recovery must never recreate the socket, or that handler is lost
  // (socket-recovery.ts).
  const unregister = socketRecovery.register(socket);

  return {
    sendMessage: (
      content: string,
      replyToMessageId?: string,
      clientMessageId?: string,
    ) =>
      socket.emit("message", {
        chatId,
        content,
        replyToMessageId,
        clientMessageId,
      }),
    sendTyping: (isTyping = true) => socket.emit("typing", { chatId, isTyping }),
    disconnect: () => {
      unregister();
      socket.emit("leave", { chatId });
      socket.disconnect();
    },
  };
}
